import React, { SFC } from "react";
import RichText from "../RichText";
import withTheme, { PropsType as WithThemeProps } from "../../hoc/withTheme";
import Header from "../Header";
import Highlight from "../Highlight";
import BlockImage from "../BlockImage";
import Image from "../Image";
import News from "../News";
import ArticleTile from "../ArticleTile";
import Cards from "../Cards";
import Card from "../Card";
import Contact from "../Contact";
import Map from "../Map";
import Video from "../Video";
import Buttons from "../Buttons";
import MainPartners from "../MainPartners";
import SecondaryPartners from '../SecondaryPartners';

type PropsType = WithThemeProps & {
    blocks: any[];
};

const ContentBlocks = (props: PropsType): SFC => {
    const blocks = props.blocks.map(block => {
        switch (block.type) {
            case "header":
                return (
                    <Header
                        key={block.id}
                        id={`block-${block.id}`}
                        title={block.title}
                        align={block.align}
                        icon={block.icon}
                        src={block.src}
                        srcSet={block.srcset}
                        theme={props.theme}
                    />
                );
            case "richText":
                return (
                    <RichText
                        key={block.id}
                        id={`block-${block.id}`}
                        contentWidth={block.contentWidth}
                        verticalSpacing={block.verticalSpacing}
                        content={block.richText}
                        theme={props.theme}
                    />
                );
            case "highlight":
                return (
                    <Highlight
                        key={`block-${block.id}`}
                        id={`block-${block.id}`}
                        contentWidth="large"
                        verticalSpacing={block.verticalSpacing}
                        theme={props.theme}
                        title={block.title}
                        text={block.text}
                        summary={block.summary}
                        align={block.align}
                        icon={block.icon}
                        button={block.button}
                    />
                );
            case "image":
                return block.mask !== undefined && block.mask !== "none" ? (
                    <BlockImage
                        key={`block-${block.id}`}
                        id={`block-${block.id}`}
                        contentWidth={block.contentWidth}
                        verticalSpacing={block.verticalSpacing}
                        image={{
                            src: block.src,
                            srcset: block.srcset,
                        }}
                        mask={block.mask}
                    />
                ) : (
                    <Image
                        key={`block-${block.id}`}
                        id={`block-${block.id}`}
                        contentWidth={block.contentWidth}
                        verticalSpacing={block.verticalSpacing}
                        src={block.src}
                        srcSet={block.srcset}
                    />
                );
            case "buttons":
                return (
                    <Buttons
                        key={`block-${block.id}`}
                        id={`block-${block.id}`}
                        contentWidth={block.contentWidth}
                        verticalSpacing={block.verticalSpacing}
                        buttons={block.buttons}
                        alignment={block.alignment}
                        theme={props.theme}
                    />
                );
            case "news":
                return (
                    <News>
                        {block.articles.map((article, index) => (
                            <ArticleTile
                                key={`article-${article.id}`}
                                id={`article-${article.id}`}
                                title={article.title}
                                text={article.text}
                                theme={article.theme}
                                image={{
                                    src: article.src,
                                    srcSet: article.srcSet,
                                }}
                                size={index > 0 ? "small" : "large"}
                                url={article.url}
                            />
                        ))}
                    </News>
                );
            case "trainings":
                return (
                    <Cards key={`block-${block.id}`} contentWidth="large" verticalSpacing="large">
                        {block.trainings.map(training => (
                            <Card
                                key={`training-${training.id}`}
                                id={`training-${training.id}`}
                                title={training.title}
                                text={training.text}
                                theme={props.theme}
                                image={{
                                    src: training.src,
                                    srcSet: training.srcSet,
                                }}
                                button={training.buttonText}
                                url={training.buttonUrl}
                                target={training.buttonTarget}
                            />
                        ))}
                    </Cards>
                );
            case "contact":
                return (
                    <Contact key={`block-${block.id}`} title={block.title} text={block.richText} form={block.form} />
                );
            case "maps":
                return <Map key={`block-${block.id}`} />;
            case "video":
                return <Video key={`block-${block.id}`} videoUrl={block.videoUrl} />;
            case "mainPartners":
                return <MainPartners key={`block-${block.id}`} partners={block.partners} verticalSpacing={block.verticalSpacing}
                theme={props.theme} />;
            case "secondaryPartners":
                return <SecondaryPartners key={`block-${block.id}`} partners={block.partners} verticalSpacing={block.verticalSpacing} 
                theme={props.theme}/>;
            default:
                return null;
        }
    });

    return blocks;
};

export default withTheme(ContentBlocks);
